import { Children, isValidElement, KeyboardEvent, RefObject, useMemo, useState, useEffect } from 'react';
import { KeyValue } from '@mews/enums';
import { TabProps } from './Tab';
import { TAB_DATA_ATTRIBUTES, TAB_NAVIGATION_KEYS } from './constants';

const findNextEnabledTab = (enabledIndices: number[], fromIndex: number, direction: 'forward' | 'backward') => {
    if (enabledIndices.length === 0) return fromIndex;

    const currentEnabledIndex = enabledIndices.indexOf(fromIndex);
    if (currentEnabledIndex === -1) return enabledIndices[0] ?? fromIndex;

    if (direction === 'forward') {
        return enabledIndices[(currentEnabledIndex + 1) % enabledIndices.length];
    }
    return enabledIndices[(currentEnabledIndex - 1 + enabledIndices.length) % enabledIndices.length];
};

const activateCurrentTab = (event: KeyboardEvent<HTMLOListElement>) => {
    event.preventDefault();
    const focusedTab = event.target;
    if (
        focusedTab instanceof HTMLElement &&
        focusedTab.hasAttribute(TAB_DATA_ATTRIBUTES.TAB_VALUE) &&
        focusedTab.getAttribute('aria-disabled') !== 'true'
    ) {
        focusedTab.click();
        requestAnimationFrame(() => {
            focusedTab.focus();
        });
    }
};

interface UseTabsKeyboardNavigationProps {
    children: Array<React.ReactElement<TabProps>> | React.ReactElement<TabProps>;
    ref: RefObject<HTMLOListElement>;
    activeTabIndex?: number;
}

export const useTabsKeyboardNavigation = ({ children, ref, activeTabIndex }: UseTabsKeyboardNavigationProps) => {
    const enabledIndices = useMemo(() => {
        const elements = Children.toArray(children).filter((child): child is React.ReactElement<TabProps> =>
            isValidElement<TabProps>(child)
        );

        return elements.map((_, index) => index).filter((index) => !elements[index].props.disabled);
    }, [children]);

    // Track which tab index should be focusable
    const [focusableTabIndex, setFocusableTabIndex] = useState<number>(0);

    // Update focusable tab when active tab changes
    useEffect(() => {
        const validActiveIndex = activeTabIndex ?? 0;
        setFocusableTabIndex(validActiveIndex >= 0 ? validActiveIndex : 0);
    }, [activeTabIndex]);

    const handleKeyDown = (event: KeyboardEvent<HTMLOListElement>) => {
        const { key } = event;

        // Handle Space/Enter early - no DOM query needed for these
        if (key === KeyValue.Spacebar || key === KeyValue.Enter) {
            activateCurrentTab(event);
            return;
        }

        // Early return for unhandled keys - no DOM query needed
        if (!TAB_NAVIGATION_KEYS.includes(key)) {
            return;
        }

        // Only query DOM when we need to navigate
        const allTabs = ref.current?.querySelectorAll(`[${TAB_DATA_ATTRIBUTES.TAB_VALUE}]`);
        if (!allTabs) return;

        // Find current focused tab index
        let currentFocusedIndex = 0;
        const focusedElement = event.target;

        if (focusedElement instanceof HTMLElement) {
            for (let i = 0; i < allTabs.length; i++) {
                const tab = allTabs[i];
                if (tab === focusedElement || tab.contains(focusedElement)) {
                    currentFocusedIndex = i;
                    break;
                }
            }
        }

        let nextIndex = currentFocusedIndex;

        switch (key) {
            case KeyValue.ArrowLeft:
            case KeyValue.ArrowUp:
                event.preventDefault();
                nextIndex = findNextEnabledTab(enabledIndices, currentFocusedIndex, 'backward');
                break;
            case KeyValue.ArrowRight:
            case KeyValue.ArrowDown:
                event.preventDefault();
                nextIndex = findNextEnabledTab(enabledIndices, currentFocusedIndex, 'forward');
                break;
            case KeyValue.Home:
                event.preventDefault();
                nextIndex = enabledIndices[0] ?? 0;
                break;
            case KeyValue.End:
                event.preventDefault();
                nextIndex = enabledIndices[enabledIndices.length - 1] ?? 0;
                break;
        }

        // Focus the next tab if it's different
        if (nextIndex !== currentFocusedIndex && nextIndex >= 0) {
            const nextTabElement = allTabs[nextIndex];
            if (nextTabElement instanceof HTMLElement) {
                nextTabElement.focus();
                setFocusableTabIndex(nextIndex);
            }
        }
    };

    return {
        handleKeyDown,
        focusableTabIndex,
    };
};
